import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CoreValuesSection.css";
import teamPhoto from "../../assests/teamPhoto.png";

const values = [
  {
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <path
          d="M18 34c0 0 4-8 14-8s14 8 14 8"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <path
          d="M10 28c2-4 6-7 10-7"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle
          cx="26"
          cy="16"
          r="5"
          stroke="#c0392b"
          strokeWidth="2.2"
        />
        <path
          d="M14 22c-3 2-5 5-5 9"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
    ),
    title: "Long-term commitment",
    desc: "8+ years of running a profitable organization gives us a good sense of challenges that a growing software business faces. We take pride in running a sustainable business that is powered by you, our customer.",
  },

  {
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <rect
          x="12"
          y="22"
          width="28"
          height="18"
          rx="3"
          stroke="#c0392b"
          strokeWidth="2.2"
        />
        <path
          d="M20 22v-5a6 6 0 0 1 12 0v5"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle cx="26" cy="31" r="2.5" fill="#c0392b" />
        <path
          d="M26 33v4"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
    ),
    title: "Privacy and security as a priority",
    desc: "We do not own or sell your data, and we most certainly do not bank on advertising-based business models. The only way we make money is from the software license fees you pay us.",
  },

  {
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <circle
          cx="26"
          cy="20"
          r="7"
          stroke="#c0392b"
          strokeWidth="2.2"
        />
        <path
          d="M14 40c0-6.627 5.373-12 12-12s12 5.373 12 12"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <path
          d="M36 16l2 2 5-5"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: "Customer-first philosophy",
    desc: "In all these years, it's our customers' trust and goodwill that has helped us establish a strong position in the market. No matter the size of your business, we're here to help you grow.",
  },

  {
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <path
          d="M26 10v6M26 36v6M10 26h6M36 26h6"
          stroke="#c0392b"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle
          cx="26"
          cy="26"
          r="8"
          stroke="#c0392b"
          strokeWidth="2.2"
        />
        <circle cx="26" cy="26" r="3" fill="#c0392b" />
      </svg>
    ),
    title: "Focus on research and development",
    desc: "Software is our craft and we back it up with our relentless investments in R&D. So much so that we prefer to own the entire technology stack, including running our own data centers globally.",
  },
];

export default function CoreValuesSection() {
  const [visible, setVisible] = useState(false);
  const [imgError, setImgError] = useState(false);
  const ref = useRef(null);

  // Navigation
  const navigate = useNavigate();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      obs.observe(ref.current);
    }

    return () => obs.disconnect();
  }, []);

  return (
    <section className="cvs-section" ref={ref}>
      {/* ── Team Photo Banner ── */}
      <div className="cvs-banner">
        {!imgError ? (
          <img
            src={teamPhoto}
            alt="SkillSync Pro team"
            className="cvs-banner-img"
            onError={() => setImgError(true)}
          />
        ) : (
          /* fallback dots if image fails */
          <div className="cvs-banner-fallback" aria-hidden="true">
            {Array.from({ length: 30 }).map((_, i) => (
              <div
                key={i}
                className="cvs-dot"
                style={{
                  width: 28 + (i % 5) * 4,
                  height: 28 + (i % 5) * 4,
                  animationDelay: `${i * 0.04}s`,
                }}
              />
            ))}
          </div>
        )}

        {/* dark gradient at bottom so white card blends */}
        <div className="cvs-banner-fade" />
      </div>

      {/* ── White Card ── */}
      <div className={`cvs-card ${visible ? "cvs-in" : ""}`}>
        <div className="cvs-heading-block">
          <h2 className="cvs-title">
            The core values and principles
            <br />
            that drive us
          </h2>

          <div className="cvs-rule" />
        </div>

        <div className="cvs-grid">
          {values.map((v, i) => (
            <div
              key={i}
              className="cvs-item"
              style={{
                animationDelay: `${0.1 + i * 0.12}s`,
              }}
            >
              <div className="cvs-icon">{v.icon}</div>

              <div className="cvs-text">
                <h3 className="cvs-item-title">{v.title}</h3>

                <p className="cvs-item-desc">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Read Our Story Button ── */}
        <div className="cvs-cta-row">
          <button
            type="button"
            className="cvs-cta-btn"
            onClick={() => navigate("/about")}
          >
            READ OUR STORY &nbsp;›
          </button>
        </div>
      </div>
    </section>
  );
}